import { useCallback, useEffect, useRef, useState } from 'react';
import type { TreatmentDatePoolEntry } from '@/clinical-study/subjects/useTreatmentDatePoolPicker';
import { apiClient } from '@/lib/api-client';
import type { CalendarSubject, InternalSchedule } from './calendar-items';

/** How many `/schedule` reads run at once (#893). */
export const SCHEDULE_READ_CONCURRENCY = 6;

/** Runs `task` over `items`, at most `limit` at a time. `task` must not reject. */
export async function runWithConcurrency<T>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<void>,
): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const item = items[next] as T;
      next += 1;
      await task(item);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

type ReadOutcome<T> = { status: 'ok'; data: T } | { status: 'denied' } | { status: 'failed' };

async function readSchedule(
  studyId: string,
  subjectId: string,
): Promise<ReadOutcome<InternalSchedule>> {
  try {
    const { data, error, response } = await apiClient.GET(
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/schedule',
      { params: { path: { studyId, subjectId } } },
    );
    if (response.status === 403) {
      return { status: 'denied' };
    }
    if (error !== undefined || !response.ok || data === undefined) {
      return { status: 'failed' };
    }
    return { status: 'ok', data };
  } catch {
    return { status: 'failed' };
  }
}

export async function readTreatmentDatePool(): Promise<ReadOutcome<TreatmentDatePoolEntry[]>> {
  try {
    const { data, error, response } = await apiClient.GET('/v1/clinical-study/treatment-date-pool');
    if (response.status === 403) {
      return { status: 'denied' };
    }
    if (error !== undefined || !response.ok || data === undefined) {
      return { status: 'failed' };
    }
    return { status: 'ok', data };
  } catch {
    return { status: 'failed' };
  }
}

export type StudyCalendarData = {
  schedules: Record<string, InternalSchedule>;
  /** Schedule reads settled so far (read, refused or failed) out of `total`. */
  settled: number;
  total: number;
  loading: boolean;
  /** Re-reading what is already shown (after `refreshToken` changed): the data stays. */
  refreshing: boolean;
  failedSubjectIds: string[];
  /** `null` while unread, refused or failed. */
  pool: TreatmentDatePoolEntry[] | null;
  poolFailed: boolean;
  retry: () => void;
};

type DataState = Omit<StudyCalendarData, 'retry' | 'loading' | 'refreshing'> & {
  poolPending: boolean;
  /** The current load re-reads shown data in place rather than starting from nothing. */
  refreshing: boolean;
  /** The study this state belongs to; `null` before any load started. */
  startedFor: string | null;
};

const EMPTY: DataState = {
  schedules: {},
  settled: 0,
  total: 0,
  failedSubjectIds: [],
  pool: null,
  poolFailed: false,
  poolPending: false,
  refreshing: false,
  startedFor: null,
};

/**
 * The study calendar's reads (#893): each enrolled subject's `/schedule`, at most
 * {@link SCHEDULE_READ_CONCURRENCY} at a time, plus the treatment date pool. Only enrolled subjects
 * are read: a provisional subject has no visits yet and a cancelled one keeps none, and their
 * treatment dates already come with the subject list.
 *
 * A read the caller's role may not make is not attempted (the permission is known) or, if refused
 * anyway (`403`), simply contributes nothing — no error. Any other failure is kept per subject so
 * the rest still shows and `retry()` re-reads only what failed.
 */
export function useStudyCalendarData({
  studyId,
  subjects,
  canReadSchedules,
  canReadPool,
  readEveryState = false,
  refreshToken = 0,
}: {
  studyId: string;
  /** `null` while the subject list is not ready. */
  subjects: readonly CalendarSubject[] | null;
  canReadSchedules: boolean;
  canReadPool: boolean;
  /** Read every subject's schedule, not only the enrolled ones' (a single subject's agenda, whose
   * list row may be older than the state its panel shows). */
  readEveryState?: boolean;
  /** A change re-reads every schedule and the pool in place, keeping what is shown until the
   * answers arrive (ui-design-guidelines § Refreshing is not loading). */
  refreshToken?: number;
}): StudyCalendarData {
  const [state, setState] = useState<DataState>(EMPTY);
  // Only the newest load may write: the study or the subject list can change mid-flight.
  const generation = useRef(0);

  const enrolledIds =
    subjects === null || !canReadSchedules
      ? []
      : subjects
          .filter((subject) => readEveryState || subject.lifecycleState === 'ENROLLED')
          .map((s) => s.id);
  const enrolledKey = enrolledIds.join(',');

  const load = useCallback(
    (
      subjectIds: readonly string[],
      withPool: boolean,
      mode: 'reset' | 'retry' | 'refresh',
      startedFor?: string,
    ) => {
      const current = generation.current + 1;
      generation.current = current;
      const live = () => generation.current === current;
      const reset = mode === 'reset';

      setState((previous) => ({
        ...(reset ? EMPTY : previous),
        schedules: reset ? {} : previous.schedules,
        settled: mode === 'retry' ? previous.settled - subjectIds.length : 0,
        total: mode === 'retry' ? previous.total : subjectIds.length,
        failedSubjectIds:
          mode === 'retry'
            ? previous.failedSubjectIds.filter((id) => !subjectIds.includes(id))
            : [],
        pool: reset ? null : previous.pool,
        poolFailed: withPool ? false : previous.poolFailed,
        poolPending: withPool,
        refreshing: mode === 'refresh',
        startedFor: startedFor ?? previous.startedFor,
      }));

      if (withPool) {
        void readTreatmentDatePool().then((outcome) => {
          if (!live()) return;
          setState((previous) => ({
            ...previous,
            poolPending: false,
            pool: outcome.status === 'ok' ? outcome.data : null,
            poolFailed: outcome.status === 'failed',
          }));
        });
      }

      void runWithConcurrency(subjectIds, SCHEDULE_READ_CONCURRENCY, async (subjectId) => {
        const outcome = await readSchedule(studyId, subjectId);
        if (!live()) return;
        setState((previous) => ({
          ...previous,
          settled: previous.settled + 1,
          schedules:
            outcome.status === 'ok'
              ? { ...previous.schedules, [subjectId]: outcome.data }
              : previous.schedules,
          failedSubjectIds:
            outcome.status === 'failed'
              ? [...previous.failedSubjectIds, subjectId]
              : previous.failedSubjectIds,
        }));
      });
    },
    [studyId],
  );

  // Only a new study (or the first read) starts from nothing. For the study already shown, a new
  // subject set, a pool permission change or a new `refreshToken` re-reads in place, so a write in
  // the panel never sends a loaded calendar back to its loading state.
  useEffect(() => {
    if (subjects === null) {
      generation.current += 1;
      setState(EMPTY);
      return;
    }
    const ids = enrolledKey === '' ? [] : enrolledKey.split(',');
    load(ids, canReadPool, state.startedFor === studyId ? 'refresh' : 'reset', studyId);
    // `subjects` is represented by `enrolledKey`: a new array with the same enrolled subjects
    // re-reads nothing unless `refreshToken` asks for it.
  }, [load, enrolledKey, canReadPool, subjects === null, refreshToken]);

  const retry = () => {
    load(state.failedSubjectIds, canReadPool && state.poolFailed, 'retry');
  };

  const pending = state.settled < state.total || state.poolPending;

  return {
    schedules: state.schedules,
    settled: state.settled,
    total: state.total,
    // Until the load for these inputs has started (the first render, a new study or subject set),
    // nothing is known yet: loading, never an empty calendar.
    loading: subjects === null || state.startedFor !== studyId || (pending && !state.refreshing),
    refreshing: subjects !== null && state.startedFor === studyId && pending && state.refreshing,
    failedSubjectIds: state.failedSubjectIds,
    pool: state.pool,
    poolFailed: state.poolFailed,
    retry,
  };
}
